import { AssembliesFragment, ReportInternalType } from '@/__generated__/graphql';
import { PointOfInterestInfo } from '@/components/Analysis/ReportsRevamped/ReportDetails/EquipmentDetails/PointOfInterestInfo';
import { AssessmentDetailsSection } from '@/components/Analysis/ReviewScreen/AssessmentDetailsSection';
import { AttachmentsSection } from '@/components/Analysis/ReviewScreen/AttachmentsSection';
import { OverviewAccordion } from '@/components/Analysis/ReviewScreen/ComponentsOverview/OverviewAccordion';
import { DetailsSection } from '@/components/Analysis/ReviewScreen/DetailsSection';
import { useGetDefectPill } from '@/components/Analysis/ReviewScreen/hooks/useGetDefectPill';
import { PoiSection } from '@/components/Analysis/ReviewScreen/PoiSection';
import { NOT_APPLICABLE_LABEL } from '@/constants';
import { useRouter } from '@/hooks';
import { abyssColors } from '@/theme/colors';
import { MappedReportPointOfInterests } from '@/types';
import { AccordionSummary, Box, Stack, Typography } from '@mui/material';
import React from 'react';
import {
  Detail,
  DetailsAccordian,
  DetailsPanel,
  DropDownIcon,
  EquipmentButton,
  EquipmentLabel,
  EquipmentName,
  EquipmentValue,
  LineBreak,
} from './styles';

type EquipmentProps = {
  equipment: AssembliesFragment;
  pointsOfInterests: MappedReportPointOfInterests[];
  reportType: ReportInternalType;
  componentId?: string;
};

type EquipmentDetailProps = {
  label: string;
  value: string;
};

const EquipmentDetail = ({ label, value }: EquipmentDetailProps) => {
  return (
    <Detail>
      <EquipmentLabel>{label}:</EquipmentLabel>
      <EquipmentValue>{value}</EquipmentValue>
    </Detail>
  );
};

export const ReportSections = ({
  equipment,
  pointsOfInterests,
  reportType,
  componentId,
}: EquipmentProps) => {
  const [detailsExpanded, setDetailsExpanded] = React.useState(false);
  const router = useRouter();
  const defectPill = useGetDefectPill({ assemblyId: equipment.id });

  const handleClick = () => {
    window.location.replace(
      `/analysis/${router.query.inspection}/viewer?assembly=${equipment?.tagName}`
    );
  };

  const equipmentDetails = React.useMemo(() => {
    return [
      {
        label: 'Spec',
        value: equipment.templateFieldData?.spec ?? NOT_APPLICABLE_LABEL,
      },
      {
        label: 'Material',
        value: equipment.templateFieldData?.material ?? NOT_APPLICABLE_LABEL,
      },
      {
        label: 'Pipe schedule',
        value: equipment.templateFieldData?.schedule ?? NOT_APPLICABLE_LABEL,
      },
      {
        label: 'Service type',
        value: equipment.templateFieldData?.group ?? NOT_APPLICABLE_LABEL,
      },
    ];
  }, [
    equipment.templateFieldData?.material,
    equipment.templateFieldData?.schedule,
    equipment.templateFieldData?.spec,
    equipment.templateFieldData?.group,
  ]);

  return (
    <Stack sx={{ '& .Mui-expanded': { margin: '0px !important' } }} id={equipment.tagName}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        width="100%"
        marginLeft={1}
      >
        <Stack direction="row" alignItems="center">
          <EquipmentName data-testid="equipment-name">{equipment.tagName}</EquipmentName>
          <LineBreak />
          <Stack direction="row" alignItems="center" gap={0.5}>
            {defectPill}
          </Stack>
        </Stack>
        <Stack direction="row" gap={1}>
          <EquipmentButton onClick={handleClick}>Open in 3D</EquipmentButton>
          <EquipmentButton
            sx={{ border: 'none' }}
            onClick={() => {
              setDetailsExpanded(!detailsExpanded);
            }}
          >
            <span style={{ margin: '0px 6px' }}>Hide details</span>
            <DropDownIcon flipVertical={detailsExpanded} />
          </EquipmentButton>
        </Stack>
      </Stack>
      <DetailsAccordian expanded={detailsExpanded} defaultExpanded square>
        <AccordionSummary aria-controls="panel-content" sx={{ display: 'none' }} />
        <DetailsPanel>
          <Stack direction="row">
            {equipmentDetails.map((item) => (
              <EquipmentDetail key={item.label} label={item.label} value={item.value ?? ''} />
            ))}
          </Stack>
        </DetailsPanel>
      </DetailsAccordian>
      {reportType === ReportInternalType.Component && (
        <Stack direction="column" gap="2rem" sx={{ width: '100%' }}>
          <Box sx={{ width: '100%', pt: 2 }}>
            <DetailsSection assemblyId={componentId ?? equipment.id} />
          </Box>
          <AssessmentDetailsSection
            assemblyId={componentId ?? equipment.id}
            componentName={equipment.tagName}
          />
          <PoiSection assemblyId={componentId ?? equipment.id} componentName={equipment.tagName} />
          <OverviewAccordion
            title="Attachments"
            details={<AttachmentsSection assemblyId={componentId ?? equipment.id} />}
            defaultExpanded={true}
            componentName={equipment.tagName}
          />
          {pointsOfInterests?.length > 0 && (
            <Typography
              sx={{ color: abyssColors.primary[500], fontWeight: 700, fontSize: '1.6rem' }}
              variant="h5"
            >
              Points of Interest:
            </Typography>
          )}
        </Stack>
      )}
      {pointsOfInterests.map((pointOfInterest) => (
        <PointOfInterestInfo data={pointOfInterest} key={pointOfInterest.id} />
      ))}
    </Stack>
  );
};
